import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API, formatApiErrorDetail } from "../lib/api";
import { AppLayout } from "../components/AppLayout";
import { StatusBadge, ModeBadge } from "../components/StatusBadge";
import { InternalNotes } from "../components/InternalNotes";
import { formatDateTime } from "../lib/format";
import { RejectedPartnersCard, CandidatesCard, DirectAssignCard, ReassignCard, ActivityLogCard } from "../components/request-detail/AssignmentCards";
import { InviiCard } from "../components/request-detail/InviiCard";
import { SectionErrorBoundary } from "../components/SectionErrorBoundary";
import { oreFM } from "./CreateRequest";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import {
  ArrowLeft, Download, User, ClipboardList, MapPin, Wallet, CheckCircle2,
  Lock, FileText, Loader2, Pencil, XCircle, Eye, PartyPopper, Copy, ArrowLeftRight, History,
} from "lucide-react";

const STATUS_TRIGGER_CLS = {
  pubblicata: "bg-[#F3ECDC] text-[#87713F] border-[#C6AD76]",
  assegnato: "bg-[#DCE3EC] text-[#18314F] border-[#8FA3BE]",
  completata: "bg-[#DCFCE7] text-[#15803D] border-[#BBF7D0]",
  da_fatturare: "bg-[#942F30] text-white border-[#7A2627]",
  annullata: "bg-[#F3DCDC] text-[#942F30] border-[#D48E8E]",
};

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-slate-100 last:border-0">
    <span className="text-xs text-slate-500">{label}</span>
    <span className="text-sm text-slate-800 font-medium text-right">{value || "—"}</span>
  </div>
);

const Block = ({ icon: Icon, title, children }) => (
  <SectionErrorBoundary title={title}>
    <Card className="p-5 border-slate-200">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="h-4 w-4 text-primary" />
        <h3 className="font-semibold text-slate-800 text-sm font-heading">{title}</h3>
      </div>
      {children}
    </Card>
  </SectionErrorBoundary>
);

export default function RequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [req, setReq] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [partners, setPartners] = useState([]);
  const [directPartner, setDirectPartner] = useState("");
  const [reassignNote, setReassignNote] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get(`/requests/${id}`);
    setReq(data);
  }, [id]);

  useEffect(() => { load(); }, [load]);
  const reassignablePartners = useMemo(
    () => partners.filter((p) => p.id !== req?.assigned_partner_id), [partners, req?.assigned_partner_id]);

  useEffect(() => {
    api.get("/partners").then(({ data }) => setPartners(data.filter((p) => p.active))).catch(() => {});
  }, []);

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/requests/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Foglio_Servizio_${req.code?.replace("/", "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("PDF non disponibile");
    }
  };

  const previewPdf = async () => {
    setPdfLoading(true);
    try {
      const res = await api.get(`/requests/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      setPdfUrl(url);
    } catch {
      toast.error("PDF non disponibile");
    } finally { setPdfLoading(false); }
  };

  const closePdf = () => {
    if (pdfUrl) window.URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
  };

  const assign = async (partnerId, opts = {}) => {
    setBusy(true);
    try {
      const params = { notify: true };
      if (opts.direct) params.direct = true;
      await api.post(`/requests/${id}/assign/${partnerId}`, null, { params });
      toast.success("Servizio assegnato al partner");
      setDirectPartner("");
      await load();
    } catch (err) {
      toast.error("Errore", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  const reassign = async (partnerId) => {
    setBusy(true);
    try {
      await api.post(`/requests/${id}/reassign/${partnerId}`, { motivo: reassignNote });
      toast.success("Partner riassegnato", { description: "Il nuovo partner è stato notificato via email." });
      setDirectPartner("");
      setReassignNote("");
      await load();
    } catch (err) {
      toast.error("Errore", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  const toggleFatturato = async (val) => {
    try {
      const { data } = await api.patch(`/requests/${id}/fatturato`, { fatturato: val });
      setReq((prev) => ({ ...prev, fatturato: data.fatturato, status: data.status || prev.status }));
      toast.success(data.fatturato ? (data.status === "completata" ? "Fatturata · stato aggiornato a Completato" : "Segnata come fatturata") : "Fatturare");
    } catch (err) {
      toast.error("Errore", { description: formatApiErrorDetail(err.response?.data?.detail) });
    }
  };

  const changeStatus = async (status) => {
    if (status === req.status) return;
    let motivo = "";
    if (status === "annullata") {
      const m = window.prompt("Motivo annullamento (facoltativo). Il partner assegnato verrà avvisato via email:", "");
      if (m === null) return;
      motivo = m;
    }
    if (status === "pubblicata" && req.assigned_partner_id) {
      if (!window.confirm("Riportare la corsa a Pubblicata? Il partner assegnato verrà rimosso e la corsa tornerà disponibile.")) return;
    }
    setBusy(true);
    try {
      await api.patch(`/requests/${id}/status`, { status, motivo });
      toast.success("Stato aggiornato");
      await load();
    } catch (err) {
      toast.error("Errore", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  const complete = async () => {
    setBusy(true);
    try {
      await api.post(`/requests/${id}/complete`);
      toast.success("Servizio completato");
      await load();
    } catch (err) {
      toast.error("Errore", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  const cancel = async () => {
    const motivo = window.prompt("Indica il motivo dell'annullamento (facoltativo). Il partner assegnato verrà avvisato via email:", "");
    if (motivo === null) return;
    setBusy(true);
    try {
      await api.post(`/requests/${id}/cancel`, { motivo });
      toast.success("Corsa annullata");
      await load();
    } catch (err) {
      toast.error("Errore", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  if (!req) return <AppLayout><div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div></AppLayout>;
  const p = req.patient || {}, o = req.operative || {}, par = req.partenza || {}, arr = req.arrivo || {}, c = req.contabilita || {};
  const ev = req.evento || {};
  const isEvento = req.request_type === "evento";
  const mapsKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const mapOrigin = [par.indirizzo, par.citta].filter(Boolean).join(", ");
  const mapDest = [arr.indirizzo, arr.citta].filter(Boolean).join(", ");
  const mapUrl = mapsKey && mapOrigin && mapDest
    ? `https://www.google.com/maps/embed/v1/directions?key=${mapsKey}&origin=${encodeURIComponent(mapOrigin)}&destination=${encodeURIComponent(mapDest)}&mode=driving`
    : null;
  const assigned = ["assegnato", "completata", "da_fatturare"].includes(req.status);
  const canPdf = req.status !== "annullata";

  return (
    <AppLayout crumbLabel={req.code}>
      <div className="max-w-5xl mx-auto nx-fade-in pb-10">
        <Button variant="ghost" size="sm" className="gap-1 mb-3 -ml-2" onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/operativa"))}>
          <ArrowLeft className="h-4 w-4" /> Elenco
        </Button>

        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold font-heading text-slate-900">{isEvento ? "Evento" : "Richiesta"} {req.code}</h1>
              <StatusBadge status={req.status} />
              {isEvento && (
                <span data-testid="detail-type-badge" className="inline-flex items-center gap-1 rounded-full bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200 px-2 py-0.5 text-xs font-medium">Evento</span>
              )}
            </div>
            <div className="mt-2"><ModeBadge mode={req.engagement_mode} /></div>
            {req.updated_at && (
              <p className="text-xs text-slate-400 mt-2" data-testid="last-modified">
                Ultima modifica: {formatDateTime(req.updated_at)}{req.updated_by ? ` · ${req.updated_by}` : ""}
              </p>
            )}
            {req.rejections > 0 && (
              <p className="text-xs text-red-600 mt-1 font-medium" data-testid="rejections-count">
                Rifiutata da {req.rejections} partner
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 mr-1">
              <span className="text-xs text-slate-500">Stato</span>
              <Select value={req.status} onValueChange={changeStatus} disabled={busy}>
                <SelectTrigger data-testid="status-select" className={`h-9 w-40 font-semibold ${STATUS_TRIGGER_CLS[req.status] || ""}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pubblicata">Pubblicata</SelectItem>
                  <SelectItem value="assegnato">Assegnato</SelectItem>
                  <SelectItem value="completata">Completata</SelectItem>
                  <SelectItem value="da_fatturare">Fatturare</SelectItem>
                  <SelectItem value="annullata">Annullata</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="duplicate-request-button" variant="outline" className="gap-2" onClick={() => navigate(isEvento ? `/operativa/nuovo-evento?duplica=${id}` : `/operativa/nuova?duplica=${id}`)}>
              <Copy className="h-4 w-4" /> Duplica
            </Button>
            {!isEvento && (
              <Button data-testid="duplicate-return-button" variant="outline" className="gap-2" onClick={() => navigate(`/operativa/nuova?duplica=${id}&ritorno=1`)}>
                <ArrowLeftRight className="h-4 w-4" /> Duplica come ritorno
              </Button>
            )}
            {req.status !== "annullata" && (
              <Button data-testid="edit-request-button" variant="outline" className="gap-2" onClick={() => navigate(isEvento ? `/operativa/evento/${id}/modifica` : `/operativa/richiesta/${id}/modifica`)}>
                <Pencil className="h-4 w-4" /> {["completata", "da_fatturare"].includes(req.status) ? "Modifica (con motivazione)" : "Modifica"}
              </Button>
            )}
            {canPdf && (
              <Button data-testid="preview-pdf-button" className="gap-2" onClick={previewPdf} disabled={pdfLoading}>
                {pdfLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />} Visualizza Modulo
              </Button>
            )}
            {canPdf && (
              <Button data-testid="download-pdf-button" variant="outline" className="gap-2" onClick={downloadPdf}>
                <Download className="h-4 w-4" /> Scarica PDF
              </Button>
            )}
            {req.status === "assegnato" && (
              <Button data-testid="complete-button" className="gap-2" onClick={complete} disabled={busy}>
                <CheckCircle2 className="h-4 w-4" /> Completa
              </Button>
            )}
            {!["completata", "da_fatturare", "annullata"].includes(req.status) && (
              <Button data-testid="cancel-request-button" variant="outline" className="gap-2 border-red-200 text-red-600 hover:bg-red-50" onClick={cancel} disabled={busy}>
                <XCircle className="h-4 w-4" /> Annulla
              </Button>
            )}
          </div>
        </div>

        {req.all_rejected && req.status === "pubblicata" && (
          <Card className="p-4 border-amber-300 bg-amber-50 mb-6 flex items-start gap-3" data-testid="all-rejected-alert">
            <XCircle className="h-5 w-5 text-amber-600 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-800">Corsa scoperta: rifiutata da tutti i partner</p>
              <p className="text-xs text-amber-700 mt-0.5">Valuta un'assegnazione diretta o contatta un partner. La Centrale è stata avvisata via email.</p>
            </div>
          </Card>
        )}

        <RejectedPartnersCard rejected={req.rejected_detail} />
        <SectionErrorBoundary title="Invii ai partner"><InviiCard req={req} partners={partners} onUpdated={setReq} /></SectionErrorBoundary>
        {req.engagement_mode === "manual" && req.status === "pubblicata" && <CandidatesCard candidates={req.candidates_detail} busy={busy} onAssign={assign} />}
        {req.status === "pubblicata" && <DirectAssignCard partners={partners} value={directPartner} onChange={setDirectPartner} busy={busy} onAssign={assign} />}
        {req.status === "assegnato" && <ReassignCard currentName={req.assigned_partner_name} partners={reassignablePartners} value={directPartner} onChange={setDirectPartner} note={reassignNote} setNote={setReassignNote} busy={busy} onReassign={reassign} />}
        <ActivityLogCard activity={req.activity} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {isEvento ? (
            <>
              <Block icon={PartyPopper} title="Dati Generali Evento">
                <Row label="Referente Operativo Nexus" value={ev.referente_nexus || o.referente} />
                <Row label="Denominazione" value={ev.denominazione} />
                <Row label="Tipo" value={ev.tipo} />
                <Row label="Luogo" value={ev.luogo} />
                <Row label="Richiedente (interno)" value={o.provenienza} />
                <Row label="Data/Ora Inizio" value={formatDateTime(ev.data_ora_inizio)} />
                <Row label="Data/Ora Fine" value={formatDateTime(ev.data_ora_fine)} />
              </Block>
              <Block icon={ClipboardList} title="Dati Operativi">
                {(ev.risorse_list && ev.risorse_list.length > 0)
                  ? ev.risorse_list.map((r) => <Row key={r.key} label={r.label} value={`${r.qty} unità`} />)
                  : <Row label="Risorse richieste" value="—" />}
                <Row label="Luogo di Stazionamento" value={ev.luogo_stazionamento} />
                <Row label="Referente Evento" value={ev.referente} />
                <Row label="Telefono Referente" value={ev.telefono_referente} />
              </Block>
            </>
          ) : (
            <>
              <Block icon={User} title="Anagrafica Paziente">
                <Row label="Nominativo" value={p.nominativo} />
                <Row label="Anni" value={p.anni} />
                <Row label="Peso (kg)" value={p.peso} />
                <Row label="Telefono" value={p.telefono} />
                <Row label="Indirizzo" value={p.indirizzo} />
                <Row label="Nato a" value={p.nato_a} />
                <Row label="Nato il" value={p.nato_il} />
                <Row label="Codice Fiscale" value={p.codice_fiscale} />
                <Row label="Email" value={p.email} />
                {req.patient_notified_at && (
                  <p data-testid="patient-notified" className="text-[11px] text-emerald-700 mt-1 flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" /> Conferma inviata al paziente il {formatDateTime(req.patient_notified_at)}
                  </p>
                )}
              </Block>

              <Block icon={ClipboardList} title="Info Operative">
                <Row label="Data/Ora Evento" value={formatDateTime(o.data_ora_evento)} />
                <Row label="Referente Nexus" value={o.referente} />
                <Row label="Richiedente" value={o.provenienza} />
                <Row label="Tipologia Mezzo" value={o.tipologia_mezzo} />
                <Row label="Mobilizzazione" value={o.mobilizzazione} />
                <Row label="Tratta" value={o.tratta} />
                <Row label="Richiesto F.M." value={o.richiesta_fm ? `SI${o.minuti_fm ? ` · ${o.minuti_fm} min di F.M. inclusi nella tariffa` : ""}` : "NO"} />
                <Row label="Orario Visita" value={o.orario_visita} />
                <Row label="Causale" value={o.causale} />
                <Row label="Necessità" value={o.necessita} />
              </Block>

              <Block icon={MapPin} title="Logistica Partenza">
                <Row label="Indirizzo" value={par.indirizzo} />
                <Row label="Città" value={par.citta} />
                <Row label="Reparto/Citofono" value={par.reparto_citofono} />
                <Row label="Piano / Scala" value={`${par.piano || "-"} / ${par.scala || "-"}`} />
                <Row label="Ascensore" value={par.ascensore} />
              </Block>

              <Block icon={MapPin} title="Logistica Arrivo">
                <Row label="Indirizzo" value={arr.indirizzo} />
                <Row label="Città" value={arr.citta} />
                <Row label="Reparto/Citofono" value={arr.reparto_citofono} />
                <Row label="Piano / Scala" value={`${arr.piano || "-"} / ${arr.scala || "-"}`} />
                <Row label="Ascensore" value={arr.ascensore} />
              </Block>
            </>
          )}

          <div className="md:col-span-2">
            <Block icon={Wallet} title="Contabilità e Note">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                <div>
                  <Row label="Importo Servizio (€)" value={c.importo} />
                  <Row label="Importo Totale (€)" value={c.importo_totale} />
                  <Row label="Distanza Tratta (km)" value={req.distanza_km} />
                  <Row label="Incasso" value={c.incasso} />
                  <Row label="Modalità Pagamento" value={c.modalita_pagamento} />
                  <Row label="Fee Nexus (€)" value={c.fee_nexus} />
                  {(
                    <div className="flex items-center justify-between py-2 text-sm border-b border-border/50">
                      <span className="text-muted-foreground">Fatturato</span>
                      <div className="flex items-center gap-2">
                        <Switch data-testid="detail-fatturato-toggle" checked={!!req.fatturato} onCheckedChange={toggleFatturato} />
                        <span className={`text-xs font-medium ${req.fatturato ? "text-emerald-600" : "text-amber-600"}`}>{req.fatturato ? "Sì" : "No"}</span>
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  {!isEvento && (
                    <>
                      <Row label="Ora Arrivo" value={c.ora_arrivo} />
                      <Row label="Ora Ripartenza" value={c.ora_ripartenza} />
                      <Row label="Ore FM" value={oreFM(c.ora_arrivo, c.ora_ripartenza)} />
                      <Row label="KM Manuali" value={c.km_manuali} />
                    </>
                  )}
                  <Row label="Note" value={c.note} />
                </div>
              </div>
            </Block>
            {(req.edit_log || []).length > 0 && (
              <Block icon={History} title="Log modifiche post-completamento">
                <ul data-testid="edit-log" className="space-y-3">
                  {[...req.edit_log].reverse().map((e, idx) => (
                    <li key={`${e.at}-${idx}`} data-testid="edit-log-entry" className="text-sm border-l-2 border-amber-300 pl-3">
                      <p className="text-xs text-slate-500">{formatDateTime(e.at)} · <span className="font-medium text-slate-700">{e.by}</span></p>
                      <p className="font-medium text-slate-800 mt-0.5">{e.reason}</p>
                      {(e.changes || []).length > 0 && (
                        <ul className="mt-1 space-y-0.5">
                          {e.changes.map((ch) => (
                            <li key={ch.field} className="text-xs text-slate-600 font-mono">
                              {ch.field}: <span className="line-through text-red-500">{String(ch.old || "—")}</span> → <span className="text-emerald-700">{String(ch.new || "—")}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              </Block>
            )}
          </div>
        </div>

        {!isEvento && mapUrl && (
          <Card className="p-5 border-slate-200 mt-4" data-testid="route-map-card">
            <div className="flex items-center gap-2 mb-3">
              <MapPin className="h-4 w-4 text-primary" />
              <h3 className="font-semibold text-slate-800 text-sm font-heading">
                Percorso {par.citta || par.indirizzo || "Partenza"} → {arr.citta || arr.indirizzo || "Arrivo"}{req.distanza_km ? ` · ${req.distanza_km} km` : ""}
              </h3>
            </div>
            <div className="rounded-lg overflow-hidden border border-slate-200">
              <iframe data-testid="route-map" title="Percorso tratta" width="100%" height="320"
                style={{ border: 0 }} loading="lazy" allowFullScreen referrerPolicy="no-referrer-when-downgrade"
                src={mapUrl} />
            </div>
            <a data-testid="route-map-link" className="inline-flex items-center gap-1 text-xs text-primary mt-2 hover:underline"
              href={`https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(mapOrigin)}&destination=${encodeURIComponent(mapDest)}&travelmode=driving`}
              target="_blank" rel="noreferrer">
              <MapPin className="h-3.5 w-3.5" /> Apri il percorso in Google Maps
            </a>
          </Card>
        )}

        {assigned && (
          <Card className="p-5 border-slate-200 mt-4 flex items-center gap-3">
            <FileText className="h-5 w-5 text-[#18314F]" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-slate-800">Partner assegnato: {req.assigned_partner_name}</p>
              <p className="text-xs text-slate-500">Il foglio di servizio PDF è disponibile per il download.</p>
            </div>
          </Card>
        )}

        <InternalNotes requestId={id} />

        <Dialog open={!!pdfUrl} onOpenChange={(o) => { if (!o) closePdf(); }}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-base">Modulo di Assegnazione — {req.code}</DialogTitle>
              <DialogDescription>Anteprima del foglio di servizio in formato PDF.</DialogDescription>
            </DialogHeader>
            <div className="w-full" data-testid="pdf-preview-body">
              {pdfUrl && (
                <object data={pdfUrl} type="application/pdf" className="w-full h-[72vh] rounded-md border border-slate-200" aria-label="modulo-pdf">
                  <iframe title="modulo-pdf" src={pdfUrl} className="w-full h-[72vh] rounded-md border border-slate-200" />
                </object>
              )}
              {pdfUrl && (
                <div className="mt-3 text-center">
                  <a href={pdfUrl} target="_blank" rel="noopener noreferrer" data-testid="pdf-open-tab" className="text-sm text-primary hover:underline">
                    Se l'anteprima non compare, apri in una nuova scheda
                  </a>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
